<script setup lang="ts">
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Execution } from '@/shared/Executions/models/Execution'

defineProps<{
  execution: Execution | null | undefined
}>()

// --- Formatting helpers ---

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getStatusBadgeClass(status?: string): string {
  switch (status) {
    case 'succeeded': return 'bg-green-500 hover:bg-green-500 text-white'
    case 'failed': return 'bg-red-500 hover:bg-red-500 text-white'
    case 'running': return 'bg-blue-500 hover:bg-blue-500 text-white'
    case 'pending': return 'bg-yellow-500 hover:bg-yellow-500 text-white'
    case 'cancelled': return 'bg-gray-500 hover:bg-gray-500 text-white'
    case 'throttled': return 'bg-orange-500 hover:bg-orange-500 text-white'
    case 'rejected': return 'bg-red-500 hover:bg-red-500 text-white'
    default: return 'bg-gray-500 hover:bg-gray-500 text-white'
  }
}
</script>

<template>
  <!-- Loading skeleton -->
  <div v-if="!execution" class="space-y-4">
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/3 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
        <Skeleton class="h-4 w-3/4" />
        <Skeleton class="h-4 w-1/2" />
        <Skeleton class="h-4 w-2/3" />
        <Skeleton class="h-4 w-1/2" />
      </div>
    </div>
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/4 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-10 w-full" />
        <Skeleton class="h-10 w-full" />
        <Skeleton class="h-10 w-full" />
      </div>
    </div>
  </div>

  <!-- Content -->
  <div v-else>
    <!-- Overall Status -->
    <div class="mb-4 rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">Overall Status</h3>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Status</dt>
          <dd>
            <Badge :class="getStatusBadgeClass(execution.status)">
              {{ execution.status ?? '—' }}
            </Badge>
          </dd>
        </div>
        <div v-if="execution.progress" class="flex justify-between">
          <dt class="text-muted-foreground">Progress</dt>
          <dd class="text-foreground">
            {{ execution.progress.percent != null ? `${execution.progress.percent}%` : '—' }}
            <span v-if="execution.progress.completedPhases != null && execution.progress.totalPhases != null" class="text-muted-foreground ml-1">
              ({{ execution.progress.completedPhases }}/{{ execution.progress.totalPhases }} phases)
            </span>
          </dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Current Phase</dt>
          <dd class="text-foreground">{{ execution.currentPhase ?? '—' }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Started</dt>
          <dd class="text-foreground">{{ formatDate(execution.startedUtc) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Completed</dt>
          <dd class="text-foreground">{{ formatDate(execution.completedUtc) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Duration</dt>
          <dd class="text-foreground">{{ execution.durationHuman ?? '—' }}</dd>
        </div>
      </dl>
    </div>

    <!-- Phases -->
    <div v-if="execution.phases && execution.phases.length > 0" class="mb-4 rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">Phases</h3>
      <div class="space-y-2">
        <div
          v-for="(phase, index) in execution.phases"
          :key="index"
          class="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
        >
          <div class="flex items-center gap-2">
            <span class="text-muted-foreground font-mono text-xs">{{ phase.order ?? index + 1 }}</span>
            <span class="text-foreground">{{ phase.phase ?? '—' }}</span>
          </div>
          <div class="flex items-center gap-3">
            <span v-if="phase.durationHuman" class="text-muted-foreground text-xs">{{ phase.durationHuman }}</span>
            <Badge :class="getStatusBadgeClass(phase.status)">
              {{ phase.status ?? '—' }}
            </Badge>
          </div>
        </div>
      </div>
    </div>

    <!-- Errors -->
    <div
      v-if="execution.errors && execution.errors.length > 0"
      class="mb-4 rounded-lg border-2 border-red-500 p-4"
    >
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
        Errors
        <Badge class="bg-red-500 hover:bg-red-500 text-white">{{ execution.errors.length }}</Badge>
      </h3>
      <div class="space-y-3">
        <div
          v-for="(err, index) in execution.errors"
          :key="index"
          class="rounded border border-red-300 bg-red-50 p-3 dark:border-red-700 dark:bg-red-950"
        >
          <dl class="space-y-1 text-sm">
            <div v-if="err.phase" class="flex gap-2">
              <dt class="text-muted-foreground min-w-[60px]">Phase:</dt>
              <dd class="text-foreground">{{ err.phase }}</dd>
            </div>
            <div v-if="err.step" class="flex gap-2">
              <dt class="text-muted-foreground min-w-[60px]">Step:</dt>
              <dd class="text-foreground">{{ err.step }}</dd>
            </div>
            <div v-if="err.message" class="flex gap-2">
              <dt class="text-muted-foreground min-w-[60px]">Message:</dt>
              <dd class="text-foreground break-all">{{ err.message }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <!-- Warnings -->
    <div
      v-if="execution.warnings && execution.warnings.length > 0"
      class="mb-4 rounded-lg border-2 border-yellow-500 p-4"
    >
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
        Warnings
        <Badge class="bg-yellow-500 hover:bg-yellow-500 text-white">{{ execution.warnings.length }}</Badge>
      </h3>
      <div class="space-y-2">
        <div
          v-for="(warning, index) in execution.warnings"
          :key="index"
          class="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-foreground dark:border-yellow-700 dark:bg-yellow-950"
        >
          {{ warning }}
        </div>
      </div>
    </div>

    <!-- No errors/warnings message -->
    <div
      v-if="(!execution.errors || execution.errors.length === 0) && (!execution.warnings || execution.warnings.length === 0)"
      class="text-sm text-muted-foreground"
    >
      No errors or warnings.
    </div>
  </div>
</template>
